// Gemeinsame Bausteine für „Stornieren statt Löschen" (Muster aus Blechbedarf):
//  · StornoDialog      — Grund ist Pflicht (min. 5 Zeichen)
//  · PapierkorbModal   — stornierte Einträge, Wiederherstellen nur Admin/Meister
//  · VerlaufListe      — wer hat wann was geändert (alt → neu), aus der *_history-Tabelle
import { useState, type ReactNode } from 'react';
import { Ban, RotateCcw, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { ErrorBanner } from './ErrorBanner';
import type { BedarfHistorie } from '../lib/api';

export function StornoDialog({ titel, onClose, onConfirm }: {
  titel: string;
  onClose: () => void;
  onConfirm: (grund: string) => Promise<void>;
}) {
  const [grund, setGrund] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const ok = grund.trim().length >= 5;
  const los = async () => {
    if (!ok) return;
    setBusy(true); setFehler('');
    try { await onConfirm(grund.trim()); }
    catch (e) { setFehler((e as Error).message); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h2 className="font-semibold text-slate-900 mb-1">{titel} stornieren</h2>
        <p className="text-sm text-slate-500 mb-3">Der Eintrag wird nicht gelöscht, sondern in den Papierkorb verschoben und bleibt im Verlauf nachvollziehbar.</p>
        <label className="block text-xs text-slate-500 mb-1">Grund * (Pflicht, mind. 5 Zeichen)</label>
        <textarea className="input w-full" rows={3} autoFocus placeholder="z. B. doppelt erfasst / nicht mehr nötig / falsch eingetragen"
                  value={grund} onChange={e => setGrund(e.target.value)} />
        {fehler && <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{fehler}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Abbrechen</button>
          <button onClick={() => void los()} disabled={busy || !ok}
                  className="inline-flex items-center gap-1.5 bg-red-600 text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
            <Ban className="w-4 h-4" /> {busy ? 'Storniert …' : 'Stornieren'}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface PapierkorbZeile { id: number; storno_grund?: string | null; storniert_am?: string | null; deleted_at?: string | null; storniert_von_name?: string | null }

export function PapierkorbModal<T extends PapierkorbZeile>({ titel, laden, restore, darfWiederherstellen, zeile, onClose, onRestored }: {
  titel: string;
  laden: () => Promise<{ data: T[] }>;
  restore: (id: number) => Promise<unknown>;
  darfWiederherstellen: boolean;
  zeile: (r: T) => ReactNode;
  onClose: () => void;
  onRestored: () => void;
}) {
  const korb = useApi(laden, []);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [fehler, setFehler] = useState('');
  const rows = korb.data?.data ?? [];
  const holen = async (id: number) => {
    setBusyId(id); setFehler('');
    try { await restore(id); korb.reload(); onRestored(); }
    catch (e) { setFehler((e as Error).message); }
    setBusyId(null);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <h2 className="font-semibold text-slate-900">Papierkorb · {titel}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Schließen"><X className="w-5 h-5" /></button>
        </div>
        {!darfWiederherstellen && (
          <div className="px-6 py-2 border-b border-slate-100 text-xs text-slate-500">Wiederherstellen dürfen nur Admin/Einkauf.</div>
        )}
        <div className="px-6 py-3 space-y-2 max-h-[60vh] overflow-y-auto">
          {korb.error && <ErrorBanner error={korb.error} onRetry={korb.reload} />}
          {fehler && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{fehler}</div>}
          {korb.loading && <div className="text-sm text-slate-400">Lädt …</div>}
          {!korb.loading && !korb.error && rows.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Papierkorb ist leer.</div>}
          {rows.map(r => (
            <div key={r.id} className="border border-slate-200 rounded-lg px-3 py-2 flex items-start justify-between gap-3">
              <div className="min-w-0 text-[13px]">
                <div className="font-semibold text-slate-800">{zeile(r)}</div>
                <div className="text-slate-700">Grund: <span className="font-medium">{r.storno_grund ?? '—'}</span></div>
                <div className="text-xs text-slate-500">{r.storniert_von_name ?? '—'} · {(r.storniert_am ?? r.deleted_at ?? '').slice(0, 16).replace('T', ' ')}</div>
              </div>
              {darfWiederherstellen && (
                <button onClick={() => void holen(r.id)} disabled={busyId === r.id}
                        className="shrink-0 inline-flex items-center gap-1 border border-emerald-600 text-emerald-700 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-emerald-50 disabled:opacity-50">
                  <RotateCcw className="w-4 h-4" /> {busyId === r.id ? '…' : 'Wiederherstellen'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const IGNORIEREN = new Set([
  'id', 'updated_at', 'updated_by_id', 'created_at', 'created_by_id', 'deleted_at', 'deleted_by_id',
  'storno_grund', 'storniert_am', 'storniert_von_id', 'erledigt_at', 'bild_key', 'storniert', 'wiederhergestellt',
]);

function anzeige(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  return s.length > 40 ? s.slice(0, 40) + '…' : s;
}

export function VerlaufListe({ laden, labels, deps }: {
  laden: () => Promise<{ data: BedarfHistorie[] }>;
  labels: Record<string, string>;
  deps: unknown[];
}) {
  const hist = useApi(laden, deps);
  const rows = hist.data?.data ?? [];
  const text = (h: BedarfHistorie): string => {
    if (h.action === 'INSERT') return 'angelegt';
    if (h.action === 'RESTORE') return 'aus dem Papierkorb wiederhergestellt';
    try {
      if (h.action === 'DELETE') {
        const n = JSON.parse(h.new_data ?? '{}') as { storno_grund?: string };
        return n.storno_grund ? `storniert: ${n.storno_grund}` : 'storniert';
      }
      const neu = JSON.parse(h.new_data ?? '{}') as Record<string, unknown>;
      const alt = JSON.parse(h.old_data ?? '{}') as Record<string, unknown>;
      const teile = Object.keys(neu)
        .filter(k => !IGNORIEREN.has(k) && (alt[k] ?? null) !== (neu[k] ?? null))
        .map(k => `${labels[k] ?? k}: ${anzeige(alt[k])} → ${anzeige(neu[k])}`);
      if (!teile.length) return 'geändert';
      return teile.slice(0, 4).join(' · ') + (teile.length > 4 ? ` · +${teile.length - 4} weitere` : '');
    } catch { return 'geändert'; }
  };
  return (
    <div className="space-y-1">
      {hist.loading && <div className="text-sm text-slate-400">Lädt …</div>}
      {hist.error && <ErrorBanner error={hist.error} onRetry={hist.reload} compact />}
      {rows.map(h => (
        <div key={h.id} className="flex gap-2 text-[13px] items-baseline">
          <span className="text-slate-400 whitespace-nowrap tabular-nums">{h.changed_at.slice(0, 16).replace('T', ' ')}</span>
          <span className="text-slate-500 whitespace-nowrap">{h.user_name ?? '—'}</span>
          <span className="text-slate-700">{text(h)}</span>
        </div>
      ))}
      {!hist.loading && !hist.error && rows.length === 0 && <div className="text-sm text-slate-400">Noch keine Einträge.</div>}
    </div>
  );
}
